import {
    Button,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography,
    withStyles,
} from '@material-ui/core';
import { useCallback, useEffect, useRef, useState } from 'react';
import { axiosInstance, cancelToken } from '../../utils/axiosInstance';
import { fwReleaseCopy as copy, endpoint, fwPairStageLabels } from '../../utils/constants';
import { isCancelled } from '../../utils/fwWorkflow';
import LoadingSpinner from '../LoadingSpinner/LoadingSpinner';
import TitleHeader from '../TitleHeader/TitleHeader';

const styles = {
    root: {
        height: '100%',
        boxSizing: 'border-box',
        padding: '1rem 2rem',
        display: 'flex',
        flexDirection: 'column',
    },
    note: { marginBottom: '0.5rem' },
    actions: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-start',
        gap: '1rem',
        marginBottom: '1rem',
    },
    table: {
        marginTop: '1rem',
        border: '1px solid #babfc7',
        borderRadius: 2,
        overflowX: 'auto',
    },
    headCell: {
        backgroundColor: '#f8f8f8',
        borderBottom: '1px solid #babfc7',
        fontWeight: 600,
        whiteSpace: 'nowrap',
    },
    cell: { borderBottom: '1px solid #dde2eb', whiteSpace: 'nowrap' },
    loadingWrapper: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '100%',
        flex: 1,
    },
};

const pad = number => String(number).padStart(2, '0');

const formatReleasedAt = value => {
    if (!value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '';
    return `${pad(date.getUTCDate())}.${pad(date.getUTCMonth() + 1)}.${date.getUTCFullYear()} ${pad(
        date.getUTCHours()
    )}:${pad(date.getUTCMinutes())}`;
};

const recorded = value => (value === null || value === undefined || value === '' ? copy.notRecorded : value);

const FwReleaseAudit = ({ classes }) => {
    const [rows, setRows] = useState([]);
    const [truncated, setTruncated] = useState(false);
    const [loading, setLoading] = useState(true);
    const [failed, setFailed] = useState(false);
    const mounted = useRef(true);
    const source = useRef(null);

    const load = useCallback(async () => {
        source.current?.cancel?.();
        const request = cancelToken.source();
        source.current = request;
        setLoading(true);
        setFailed(false);
        try {
            const response = await axiosInstance.get(`${endpoint.FW_READ_MODEL}/releases?limit=200`, {
                cancelToken: request.token,
            });
            const data = response?.data?.data;
            if (!mounted.current || request !== source.current) return;
            if (!data || !Array.isArray(data.rows)) {
                setRows([]);
                setTruncated(false);
                setFailed(true);
                return;
            }
            setRows(data.rows);
            setTruncated(Boolean(data.truncated));
        } catch (error) {
            if (!mounted.current || request !== source.current || isCancelled(error)) return;
            setRows([]);
            setTruncated(false);
            setFailed(true);
        } finally {
            if (mounted.current && request === source.current) setLoading(false);
        }
    }, []);

    useEffect(() => {
        mounted.current = true;
        load();
        return () => {
            mounted.current = false;
            source.current?.cancel?.();
        };
    }, [load]);

    return (
        <div className={classes.root}>
            <TitleHeader title={`Moduł FW — ${copy.title}`} />
            <Typography className={classes.note}>{copy.explanation}</Typography>
            <Typography className={classes.note}>{copy.bounds}</Typography>
            <div className={classes.actions}>
                <Button variant="outlined" onClick={load} disabled={loading}>
                    {copy.reload}
                </Button>
            </div>
            {loading && (
                <div className={classes.loadingWrapper}>
                    <LoadingSpinner />
                </div>
            )}
            {!loading && failed && (
                <Typography className={classes.note} color="error" role="alert">
                    {copy.readError}
                </Typography>
            )}
            {!loading && !failed && truncated && (
                <Typography className={classes.note}>{copy.truncated}</Typography>
            )}
            {!loading && !failed && rows.length === 0 && (
                <Typography className={classes.note}>{copy.empty}</Typography>
            )}
            {!loading && !failed && rows.length > 0 && (
                <div className={classes.table}>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell className={classes.headCell}>{copy.columns.releasedAt}</TableCell>
                                <TableCell className={classes.headCell}>{copy.columns.artifact}</TableCell>
                                <TableCell className={classes.headCell}>{copy.columns.pair}</TableCell>
                                <TableCell className={classes.headCell}>{copy.columns.attempt}</TableCell>
                                <TableCell className={classes.headCell}>{copy.columns.serial}</TableCell>
                                <TableCell className={classes.headCell}>{copy.columns.stage}</TableCell>
                                <TableCell className={classes.headCell}>{copy.columns.operator}</TableCell>
                                <TableCell className={classes.headCell}>{copy.columns.reason}</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {rows.map(row => (
                                <TableRow key={row.attempt_id}>
                                    <TableCell className={classes.cell}>
                                        {formatReleasedAt(row.released_at)}
                                    </TableCell>
                                    <TableCell className={classes.cell}>
                                        {recorded(row.file_name)}
                                        {row.family ? ` (${row.family})` : ''}
                                    </TableCell>
                                    <TableCell className={classes.cell}>{recorded(row.pair_id)}</TableCell>
                                    <TableCell className={classes.cell}>{recorded(row.attempt_id)}</TableCell>
                                    <TableCell className={classes.cell}>
                                        {recorded(row.serial_number)}
                                    </TableCell>
                                    <TableCell className={classes.cell}>
                                        {fwPairStageLabels[row.stage] ?? recorded(row.stage)}
                                    </TableCell>
                                    <TableCell className={classes.cell}>
                                        {recorded(row.released_by)}
                                    </TableCell>
                                    <TableCell className={classes.cell}>{recorded(row.reason)}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </div>
            )}
        </div>
    );
};

export default withStyles(styles)(FwReleaseAudit);
